import { recordOriginalGeometryPositions } from './meshOriginalGeometry';
import {
  getBoxInObjectSpace,
  recomputeGeometryBounds,
  setVertexFromObjectLocal,
} from './objectSpaceGeometry';
import {
  isCabinetSectionHighlightBox,
  withSectionHighlightBoxesHidden,
} from '../cabinetInterior/tallCabinetSectionHighlightBoxes';

// Ratio of original width/depth kept near each end before stretching the middle.
const DEFAULT_SIDE_BAND_RATIO = 0.12;

function recordWidthDepthBounds(instance, boundsTag) {
  if (instance.userData[boundsTag]) {
    return instance.userData[boundsTag];
  }

  const box = withSectionHighlightBoxesHidden(instance, () => {
    instance.updateMatrixWorld(true);
    return getBoxInObjectSpace(instance, instance);
  });
  const bounds = {
    minX: box.min.x,
    maxX: box.max.x,
    width: box.max.x - box.min.x,
    minZ: box.min.z,
    maxZ: box.max.z,
    depth: box.max.z - box.min.z,
  };

  instance.userData[boundsTag] = bounds;
  return bounds;
}

// One-dimensional 9-slice used for regular rectangular cabinet width/depth resizing.
function getResizedAxisValue(value, sourceMin, sourceMax, targetSize, sideBandRatio) {
  const sourceSize = sourceMax - sourceMin;
  if (!sourceSize || sourceSize <= 0 || !targetSize || targetSize <= 0) {
    return value;
  }

  const sourceBandSize = sourceSize * sideBandRatio;
  const targetBandSize = Math.min(sourceBandSize, targetSize * 0.45);
  const sourceMinBandEnd = sourceMin + sourceBandSize;
  const sourceMaxBandStart = sourceMax - sourceBandSize;
  const targetMax = sourceMin + targetSize;
  const targetMinBandEnd = sourceMin + targetBandSize;
  const targetMaxBandStart = targetMax - targetBandSize;

  if (value <= sourceMinBandEnd) {
    return sourceMin + ((value - sourceMin) * (targetBandSize / sourceBandSize));
  }

  if (value >= sourceMaxBandStart) {
    return targetMax - ((sourceMax - value) * (targetBandSize / sourceBandSize));
  }

  const sourceMiddleSize = sourceMaxBandStart - sourceMinBandEnd;
  const targetMiddleSize = targetMaxBandStart - targetMinBandEnd;
  if (sourceMiddleSize <= 0 || targetMiddleSize < 0) {
    return value;
  }

  const middleRatio = (value - sourceMinBandEnd) / sourceMiddleSize;
  return targetMinBandEnd + (middleRatio * targetMiddleSize);
}

// Recomputes mesh vertices from cached original geometry so repeated slider changes do not accumulate distortion.
function resizeMeshWidthDepth(mesh, instance, originalBounds, targetWidth, targetDepth, options) {
  const originalGeometry = recordOriginalGeometryPositions(mesh, options.geometryTag, null, null);
  if (!originalGeometry) {
    return;
  }

  mesh.updateMatrixWorld(true);

  const THREE = window.THREE;
  const vertex = new THREE.Vector3();
  const originalVertex = new THREE.Vector3();
  const currentVertex = new THREE.Vector3();
  const instanceLocalVertex = new THREE.Vector3();
  const position = mesh.geometry.attributes.position;

  for (let index = 0; index < originalGeometry.positions.length; index += 3) {
    originalVertex.set(
      originalGeometry.positions[index],
      originalGeometry.positions[index + 1],
      originalGeometry.positions[index + 2]
    );
    currentVertex.set(
      position.array[index],
      position.array[index + 1],
      position.array[index + 2]
    );

    mesh.localToWorld(originalVertex);
    instance.worldToLocal(originalVertex);
    mesh.localToWorld(currentVertex);
    instance.worldToLocal(currentVertex);

    instanceLocalVertex.copy(currentVertex);
    instanceLocalVertex.x = getResizedAxisValue(
      originalVertex.x,
      originalBounds.minX,
      originalBounds.maxX,
      targetWidth,
      options.sideBandRatio
    );
    instanceLocalVertex.z = getResizedAxisValue(
      originalVertex.z,
      originalBounds.minZ,
      originalBounds.maxZ,
      targetDepth,
      options.sideBandRatio
    );

    setVertexFromObjectLocal(mesh, instance, vertex, instanceLocalVertex);

    position.array[index] = vertex.x;
    position.array[index + 1] = vertex.y;
    position.array[index + 2] = vertex.z;
  }

  position.needsUpdate = true;
  recomputeGeometryBounds(mesh.geometry);
}

// Resizes regular cabinet models by changing x/z extents while preserving end bands.
export function resizeModelWidthDepth(instance, dimensions, options = {}) {
  const normalizedInitialWidth = Number(dimensions.initialWidth);
  const normalizedWidth = Number(dimensions.width);
  const normalizedInitialDepth = Number(dimensions.initialDepth);
  const normalizedDepth = Number(dimensions.depth);

  if (
    !instance
    || !normalizedInitialWidth
    || !normalizedWidth
    || !normalizedInitialDepth
    || !normalizedDepth
    || normalizedInitialWidth <= 0
    || normalizedWidth <= 0
    || normalizedInitialDepth <= 0
    || normalizedDepth <= 0
  ) {
    return;
  }

  const resizeOptions = {
    boundsTag: options.boundsTag || 'modelWidthDepthOriginalBounds',
    geometryTag: options.geometryTag || 'modelWidthDepthOriginalGeometry',
    sideBandRatio: options.sideBandRatio || DEFAULT_SIDE_BAND_RATIO,
  };
  const originalBounds = recordWidthDepthBounds(instance, resizeOptions.boundsTag);
  if (
    !originalBounds.width
    || originalBounds.width <= 0
    || !originalBounds.depth
    || originalBounds.depth <= 0
  ) {
    return;
  }

  const targetWidth = originalBounds.width * (normalizedWidth / normalizedInitialWidth);
  const targetDepth = originalBounds.depth * (normalizedDepth / normalizedInitialDepth);

  instance.traverse((child) => {
    if (child.isMesh && !isCabinetSectionHighlightBox(child)) {
      resizeMeshWidthDepth(child, instance, originalBounds, targetWidth, targetDepth, resizeOptions);
    }
  });
}
